<template>
  <div v-if="currentUser">
    <component-actions :title="$t('notifications.listing.listingTitle')">
      <template v-slot:actionButtons>
        <tol-button
          button-type="minor"
          icon-name="envelope-open"
          class="notification-action"
          :class="{ active: isActive }"
          @click="updateNotificationStatus('READ')"
        >
        </tol-button>
        <tol-button
          button-type="minor"
          icon-name="envelope"
          class="notification-action"
          :class="{ active: isActive }"
          @click="updateNotificationStatus('UNREAD')"
        >
        </tol-button>
        <tol-button
          button-type="minor"
          icon-name="trash-alt"
          class="notification-action"
          :class="{ active: isActive }"
          @click="deleteSelected"
        >
        </tol-button>
      </template>
    </component-actions>
    <tol-table v-if="notifications.length" :table-data="notifications" :columns="columns">
      <template v-slot:tableHeader>
        <th title="Select All"><Checkbox :checked="allSelected" @change="onToggleSelectAll" /></th>
        <th></th>
        <th>{{ $t('notifications.listing.labels.timeGenerated') }}</th>
        <th>{{ $t('notifications.listing.labels.generatedBy') }}</th>
        <th>{{ $t('notifications.listing.labels.referenceNumber') }}</th>
        <th>{{ $t('notifications.listing.labels.message') }}</th>
      </template>
      <template v-slot:generatedAt="{ value }">
        {{ value.generatedAt | formatDateTime }}
      </template>
      <template v-slot:lastName="{ value }">
        {{ value.lastName }}, {{ value.firstName }}
      </template>
      <template v-slot:check="{ value }">
        <td>
          <Checkbox :checked="value.isSelected" @change="onToggleSelectNotification(value.id)" />
        </td>
      </template>
      <template v-slot:objectType="{ value }">
        <span class="object-url">
          <router-link :to="getObjectUrl(value)" @click.native="setNotificationAsRead(value)">
            {{ value.objectType | readable | capitalize }}
            {{ value.action | capitalize | pasttense }}
            {{ value.objectLabel }}
          </router-link>
        </span>
      </template>
      <template v-slot:objectReferenceNumber="{ value }">
        <router-link :to="getObjectUrl(value)" @click.native="setNotificationAsRead(value)">
          {{ value.objectReferenceNumber }}
        </router-link>
      </template>

      <template v-slot:extra="{ value }">
        <td>
          <font-awesome-icon
            v-if="!value.readAt"
            icon="envelope"
            class="unread"
            @click="onToggleReadNotification(value, 'READ')"
          />
          <font-awesome-icon
            v-else
            icon="envelope-open"
            class="read"
            @click="onToggleReadNotification(value, 'UNREAD')"
          />
        </td>
      </template>
    </tol-table>
    <div v-else class="empty">{{ $t('notifications.listing.noNotificationMessage') }}</div>
  </div>
</template>

<script>
import TolTable from '@/app/components/TolTable';
import ComponentActions from '@/app/components/listing/ComponentActions';
import TolButton from '@/app/components/TolButton';
import { NotificationsListingActions } from '@/app/features/notifications/features/notifications-listing/notifications-listing.keys';
import { AppActions } from '@/app/app.keys';
import Checkbox from '@/app/components/Checkbox';

export default {
  components: {
    TolTable,
    Checkbox,
    ComponentActions,
    TolButton
  },
  data() {
    return {
      columns: ['generatedAt', 'lastName', 'objectReferenceNumber', 'objectType']
    };
  },
  computed: {
    currentUser: {
      get() {
        return this.$store.state.currentUser;
      }
    },
    notifications: {
      get() {
        return this.$store.getters.Notifications;
      }
    },
    isActive() {
      return this.$store.getters.IsAnyNotificationChecked;
    },
    allSelected() {
      return this.$store.getters.AreAllNotificationsSelected;
    }
  },
  mounted() {
    this.$store.dispatch(AppActions.SetPageTitle, this.$t('notifications.listing.title'));
    this.$store.dispatch(NotificationsListingActions.RetrieveNotifications);
  },
  methods: {
    updateNotificationStatus(status) {
      if (this.isActive) {
        this.$store.dispatch(NotificationsListingActions.SetNotificationStatus, {
          notificationStatus: status
        });
      }
    },
    deleteSelected() {
      if (this.isActive) {
        this.$store.dispatch(NotificationsListingActions.DeleteSelectedNotifications);
      }
    },
    onToggleSelectNotification(event) {
      this.$store.dispatch(NotificationsListingActions.SelectNotification, event);
    },
    onToggleSelectAll() {
      const isChecked = !this.allSelected;
      this.$store.dispatch(NotificationsListingActions.SelectNotification, { checked: isChecked });
    },
    onToggleReadNotification(val, status) {
      this.$store.dispatch(NotificationsListingActions.SetNotificationStatus, {
        notificationStatus: status,
        notificationId: val.id
      });
    },
    getObjectUrl(val) {
      switch (val.objectType) {
        case 'TIME_SHEET':
          return `/time-sheets/${val.objectReferenceNumber}`;
        case 'EXPENSE_REPORT':
          return `/expenses/${val.objectReferenceNumber}`;
        case 'ASSIGNMENT':
          return `/assignments/${val.objectReferenceNumber}`;
        case 'PERM_PLACEMENT':
          return `/perm-placements/${val.objectReferenceNumber}`;
      }
    },
    setNotificationAsRead(val) {
      this.$store.dispatch(NotificationsListingActions.SetNotificationStatus, {
        notificationStatus: 'READ',
        notificationId: val.id
      });
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/app/styles/colors.scss';

.notification-action {
  width: 35px;
  height: 35px;
  color: $light-grey-1;
  padding-left: 8px;
  border: 1px solid $light-grey-1;
  border-radius: 50%;
  margin-right: 8px;
}

.read-status:hover {
  cursor: pointer;
}

.active {
  color: $black;
  &:hover {
    border: 1px solid $blue;
    color: $red;
  }
}

.unread {
  color: $blue;
}

.read {
  color: $light-grey-1;
}

.read:hover,
.unread:hover {
  cursor: pointer;
}

.object-url,
.object-url:hover {
  cursor: pointer;
  color: $blue;
  a {
    text-decoration: none;
  }
}

.empty {
  width: 100%;
  margin: 0 auto;
  padding: 60px;
  background-color: $white;

  border-top: 1px solid $light-grey-1;
}
</style>
